import { useEffect, useState } from "react";
import { Spinner } from "../../shared/ui";

// ── Stroop Host Screen — Не верь словам ──────────────────────────────────────

const HEART = "❤️";
const SKULL = "💀";

function LivesDisplay({ lives, max = 3, color, tv }) {
  return (
    <div style={{ display: "flex", gap: "2px" }}>
      {Array.from({ length: max }).map((_, i) => (
        <span key={i} style={{ fontSize: tv ? "1.1rem" : "0.8rem", opacity: i < (lives ?? max) ? 1 : 0.2 }}>
          {i < (lives ?? max) ? HEART : "🖤"}
        </span>
      ))}
    </div>
  );
}

export default function StroopHost({ question, result, players, scores, lives, tv }) {
  const [timeLeft, setTimeLeft] = useState(null);
  const [prevQ, setPrevQ] = useState(null);

  // Countdown
  useEffect(() => {
    if (!question) return;
    if (question.qnum !== prevQ) {
      setPrevQ(question.qnum);
      setTimeLeft(question.time);
    }
  }, [question, prevQ]);

  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) return;
    const t = setTimeout(() => setTimeLeft(n => Math.max(0, n - 1)), 1000);
    return () => clearTimeout(t);
  }, [timeLeft]);

  // Reset timer when result arrives
  useEffect(() => {
    if (result) setTimeLeft(null);
  }, [result]);

  // ── Result screen ──────────────────────────────────────────────────────────
  if (result) {
    const correct = result.ink; // correct = ink color
    return (
      <div className="w-full animate-slide-up">
        {/* Round header */}
        <div className="text-center mb-4">
          <span className="font-heading text-[#aaa]" style={{ fontSize: tv ? "1rem" : "0.75rem" }}>
            Вопрос {result.qnum} / {result.total}
          </span>
        </div>

        {/* The word shown */}
        <div className="text-center mb-4">
          <div style={{
            display: "inline-block",
            background: "#0a0a14",
            border: "3px solid #222",
            borderRadius: "1.5rem",
            padding: tv ? "1.5rem 3rem" : "1rem 2rem",
            boxShadow: "6px 6px 0 #000",
          }}>
            <div className="font-heading text-[#555] mb-1" style={{ fontSize: tv ? "0.9rem" : "0.65rem" }}>
              Было написано:
            </div>
            <div className="font-display" style={{
              fontSize: tv ? "4rem" : "2.5rem",
              color: result.ink.hex,
              textShadow: `0 0 20px ${result.ink.hex}88`,
              letterSpacing: "0.05em",
            }}>
              {result.word.label}
            </div>
            <div className="font-heading mt-2" style={{ fontSize: tv ? "1rem" : "0.7rem", color: "#555" }}>
              Правильный ответ: <span style={{ color: correct.hex }}>{correct.label}</span>
            </div>
          </div>
        </div>

        {/* Player results */}
        <div style={{
          maxWidth: tv ? "700px" : "100%",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: "0.4rem",
        }}>
          {players.map(p => {
            const ans = result.answers[p.name];
            const isCorrect = ans === result.correct;
            const isWrong = ans && ans !== result.correct;
            const noAnswer = !ans;
            const isElim = (result.lives[p.name] ?? 3) <= 0;
            const pts = result.pts[p.name];

            return (
              <div key={p.name} style={{
                background: isElim ? "#1a0000" : "#1a1030",
                border: `2px solid ${isCorrect ? "#3DFF3D" : isWrong ? "#FF3355" : "#333"}`,
                borderRadius: "0.75rem",
                padding: tv ? "0.6rem 1rem" : "0.4rem 0.75rem",
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
                opacity: isElim ? 0.5 : 1,
              }}>
                {/* Status icon */}
                <span style={{ fontSize: tv ? "1.3rem" : "1rem", minWidth: "1.5rem" }}>
                  {isElim ? SKULL : isCorrect ? "✅" : isWrong ? "❌" : "⏱️"}
                </span>

                {/* Name */}
                <span className="font-heading text-white flex-1" style={{ fontSize: tv ? "1.3rem" : "0.9rem" }}>
                  {p.name}
                </span>

                {/* Their answer */}
                  {ans && (
                  <span className="font-heading" style={{
                    fontSize: tv ? "0.9rem" : "0.65rem",
                    color: result.answers[p.name] === result.correct
                      ? "#3DFF3D" : "#FF3355",
                  }}>
                    → {(result.colors || []).find(c => c.id === ans)?.label || ans.toUpperCase()}
                  </span>
                )}

                {/* Points delta */}
                {pts > 0 && (
                  <span className="font-display" style={{ color: "#3DFF3D", fontSize: tv ? "1.1rem" : "0.8rem" }}>
                    +{pts}
                  </span>
                )}
                {pts === -1 && (
                  <span className="font-display" style={{ color: "#FF3355", fontSize: tv ? "1.1rem" : "0.8rem" }}>
                    -❤️
                  </span>
                )}

                {/* Lives */}
                <LivesDisplay lives={result.lives[p.name]} max={3} tv={tv} />

                {/* Total score */}
                <span className="font-display" style={{ color: p.color, fontSize: tv ? "1rem" : "0.75rem", minWidth: tv ? "3rem" : "2rem", textAlign: "right" }}>
                  {result.scores[p.name] || 0}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Question screen ────────────────────────────────────────────────────────
  if (!question) return <Spinner color="#FF3355" tv={tv} />;

  const timerColor = timeLeft > 2 ? "#3DFF3D" : "#FF3355";

  return (
    <div className="w-full text-center animate-slide-up">
      {/* Header: qnum — timer — placeholder для симметрии */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: tv ? "1rem" : "0.5rem" }}>
        <span className="font-heading text-[#555]" style={{ fontSize: tv ? "1rem" : "0.75rem", minWidth: tv ? "6rem" : "4rem" }}>
          Вопрос {question.qnum} / {question.total}
        </span>
        <span className="font-display" style={{ color: timerColor, fontSize: tv ? "2rem" : "1.4rem", transition: "color 0.3s" }}>
          {timeLeft}
        </span>
        <span style={{ minWidth: tv ? "6rem" : "4rem" }} />
      </div>

      {/* THE WORD — shown in wrong color */}
      <div style={{
        display: "inline-block",
        background: "#0a0a14",
        border: `3px solid ${question.ink.hex}44`,
        borderRadius: "2rem",
        padding: tv ? "2rem 4rem" : "1.25rem 2.5rem",
        boxShadow: `0 0 40px ${question.ink.hex}22, 6px 6px 0 #000`,
        marginBottom: tv ? "1.5rem" : "1rem",
      }}>
        <div className="font-heading text-[#333] mb-2" style={{ fontSize: tv ? "0.9rem" : "0.65rem" }}>
          {question.task_type === "word"
            ? <span style={{ color: "#FFE600" }}>⚠️ ПРОЧИТАЙ СЛОВО! Игнорируй цвет!</span>
            : "Какого цвета этот текст?"}
        </div>
        <div className="font-display" style={{
          fontSize: tv ? "5rem" : "3rem",
          color: question.ink.hex,
          textShadow: `0 0 30px ${question.ink.hex}`,
          letterSpacing: "0.06em",
          lineHeight: 1,
        }}>
          {question.word.label}
        </div>
      </div>

      {/* Players status */}
      <div className="flex flex-wrap justify-center gap-2">
        {players.map(p => {
          const isElim = (question.lives[p.name] ?? 3) <= 0;
          return (
            <div key={p.name} style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "2px",
              opacity: isElim ? 0.35 : 1,
            }}>
              <div className="player-chip" style={{
                backgroundColor: isElim ? "#333" : p.color,
                fontSize: tv ? "1rem" : "0.75rem",
                padding: tv ? "0.3rem 0.9rem" : "0.2rem 0.6rem",
              }}>
                {isElim ? "💀 " : ""}{p.name}
              </div>
              <LivesDisplay lives={question.lives[p.name]} max={3} tv={tv} />
            </div>
          );
        })}
      </div>
    </div>
  );
}